import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Registration switch client (round 13, R14). Four RPCs; none of these ever
// throws — a failure is `{ ok: false, error }` so the caller can pick copy.
//   get_registration_status  — anon-callable, exactly `{ open: boolean }`
//   register_interest        — anon-callable, `{ ok: true }` | `{ ok: false, error }`
//   admin_set_registration   — admin only, returns `{ open }`
//   admin_interest_registrations — admin only, `{ count, rows: [{ email, consented_at, created_at }] }`
// A status reply that is not an object with a strictly boolean `open` is a
// failure (the auth screen treats a failed status as closed). No email is ever
// logged or stored by this module.
// ─────────────────────────────────────────────────────────────────────────────

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** The signed-out visitor's view of the switch: `{ ok: true, open }` or `{ ok: false, error }`. */
export async function fetchRegistrationStatus() {
  try {
    const { data, error } = await supabase.rpc("get_registration_status");
    if (error) return { ok: false, error: "server_error" };
    if (!isObject(data) || typeof data.open !== "boolean") return { ok: false, error: "malformed" };
    return { ok: true, open: data.open };
  } catch {
    return { ok: false, error: "network" };
  }
}

/** Leave an email for the launch mailing list. Returns the RPC's JSON, or `{ ok: false, error }`. */
export async function registerInterest(email, consent) {
  try {
    const { data, error } = await supabase.rpc("register_interest", { p_email: email, p_consent: consent === true });
    if (error) return { ok: false, error: "server_error" };
    if (!isObject(data) || typeof data.ok !== "boolean") return { ok: false, error: "server_error" };
    return data;
  } catch {
    return { ok: false, error: "network" };
  }
}

/** Admin: open or close sign-ups. Returns `{ ok: true, open }` or `{ ok: false, error }`. */
export async function adminSetRegistration(open) {
  try {
    const { data, error } = await supabase.rpc("admin_set_registration", { p_open: open });
    if (error) return { ok: false, error: error.code === "42501" ? "forbidden" : "server_error" };
    if (!isObject(data) || typeof data.open !== "boolean") return { ok: false, error: "malformed" };
    return { ok: true, open: data.open };
  } catch {
    return { ok: false, error: "network" };
  }
}

/** Admin: every address on the interest list, newest first. `{ ok: true, count, rows }` or `{ ok: false, error }`. */
export async function adminInterestRegistrations() {
  try {
    const { data, error } = await supabase.rpc("admin_interest_registrations");
    if (error) return { ok: false, error: error.code === "42501" ? "forbidden" : "server_error" };
    if (!isObject(data) || !Array.isArray(data.rows)) return { ok: false, error: "malformed" };
    return { ok: true, count: Number.isFinite(data.count) ? data.count : data.rows.length, rows: data.rows };
  } catch {
    return { ok: false, error: "network" };
  }
}
